<template>
    <div data-cy="wing-toppings" v-if="hasAvailableFlavors">
        <ion-card-title class="mx-8 mb-8 ion-text-center bold">{{ 'Choose Your ' + (initialItem?.isAppetizer ? 'Sauce Cup' : 'Flavor') }}</ion-card-title>
        <div>
            <div v-if="!isCartLoader || isLoader">
                <radiolist key="SauceOrFlavor" :options="availableTopping?.flavors ?? []" keyName="name" dense v-model="localFlavor" data-cy="choose-flavor" />
            </div>
            <div v-else>
                <dynamicSkeleText element="div" :childElements="[{ width: 60, height: '24px', class: 'mx-a mb-8' }]" />
                <dynamicSkeleText v-for="i in Array.from({ length: 4 }, (_, i) => i + 1)" :key="i" element="div" :childElements="skeleText" />
            </div>
        </div>

        <ion-item-divider class="my-8" v-if="hasAvailableDressings" />

        <ion-card-title class="mx-8 ion-margin-top ion-text-center bold" v-if="hasAvailableDressings">Dipping Cup?</ion-card-title>
        <div class="ion-text-center bold" v-if="hasAvailableDressings">(Up to {{ cupLimit }} included)</div>

        <div class="ion-margin" v-if="!isCartLoader || isLoader">
            <multiSelectQuantity :options="getToppingOptions(availableTopping?.dressings ?? [])" @selectionUpdated="updateChickenToppings" />
        </div>
        <div class="ion-margin-vertical" v-else>
            <dynamicSkeleText v-for="i in Array.from({ length: 6 }, (_, i) => i + 1)" :key="i" element="div" :childElements="skeleText" />
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, watch, computed, ref, onMounted } from 'vue';
import { IonCardTitle, IonItemDivider } from '@ionic/vue';
import radiolist from '@/components/inputs/radiolist.vue';
import baseToppings, { passThroughBindings } from '@/components/menu/toppings/baseToppings.vue';
import dynamicSkeleText from '@/components/dynamicSkeleText.vue';
import { skeleText } from '@/models/props/skeleText';
import { useStore } from 'vuex';
import multiSelectQuantity from '@/components/inputs/multiSelectQuantity.vue';
import { Topping } from '@/models/menuModels';

export default defineComponent({
    extends: baseToppings,
    components: { IonCardTitle, IonItemDivider, radiolist, dynamicSkeleText, multiSelectQuantity },
    setup(props, { emit }) {
        const store = useStore();
        const { localFlavor } = passThroughBindings(props, emit);
        const skeleText: skeleText[] = [
            { width: 5, height: '24px', display: 'inline-block', marginRight: '5px' },
            { width: 30, height: '24px', display: 'inline-block', marginRight: '5px' },
            { width: 20, height: '24px', display: 'inline-block', marginRight: '5px', background: 'transparent' },
            { width: 35, height: '24px', display: 'inline-block', marginRight: '5px' },
        ];
        const hasAvailableFlavors = computed(() => props.availableTopping?.flavors && props.availableTopping.flavors.length > 0);
        const hasAvailableDressings = computed(() => props.availableTopping?.dressings && props.availableTopping?.dressings.length > 0);
        const isLoader = computed(() => store.getters.getFlavorLoader);
        const isCartLoader = computed(() => store.getters.getCartEditLoader);
        const piecesToMaxCups = {
            3: 1,
            5: props.initialItem?.isWing ? 1 : 2,
            6: 1,
            10: 1,
            12: props.initialItem?.isWing ? 1 : 4,
            25: 2,
            30: 2,
        };

        const getToppingOptions = (toppings: Array<Topping> | []) => {
            const toppingsFiltered = toppings?.filter((topping) => topping.name != 'No Dressing');
            let toppingOptions = toppingsFiltered.map((topping) => {
                return {
                    value: topping.name,
                    limit: topping.max,
                    quantity: 0,
                };
            });

            if (Array.isArray(props.dressing)) {
                props.dressing.forEach((dressing) => {
                    let matchingOption = toppingOptions.find((option) => option.value === dressing.name);
                    if (matchingOption) {
                        matchingOption.quantity++;
                    }
                });
            }
            return toppingOptions;
        };

        const selectedToppings = computed(() => (Array.isArray(props.dressing) ? props.dressing : []));
        const selectedToppingOptions = computed(() =>
            selectedToppings.value.map((topping) => {
                return { name: topping.name, limit: topping.max };
            })
        );
        const cupLimit = computed(() => piecesToMaxCups[props.selectedSize?.number ?? 3]);

        const updateChickenToppings = (selections) => {
            const newSelectedToppingOptions = selections;

            let newSelectedToppings: Array<Topping> = [];
            newSelectedToppingOptions.forEach((option) => {
                const matchingTopping = props.availableTopping?.dressings.find((dressing) => dressing.name === option.value);

                if (matchingTopping) {
                    const optionArray: Array<Topping> = Array(option.quantity);
                    optionArray.fill(matchingTopping);
                    newSelectedToppings = newSelectedToppings.concat(optionArray);
                }
            });

            emit('update:dressing', newSelectedToppings);
        };

        emit('blockSubmit', true);

        onMounted(() => {
            const flavors = props.availableTopping?.flavors;
            localFlavor.value = localFlavor.value ?? (flavors?.length === 1 || props.initialItem?.isAppetizer) ? flavors?.[0] : undefined;
        });

        watch(
            () => isLoader.value,
            (val) => {
                //@ts-ignore
                if (val == true) {
                    store.dispatch('cartEditLoader', false);
                }
            }
        );

        watch(
            () => props.availableTopping,
            (update) => {
                setTimeout(() => {
                    localFlavor.value = update?.flavors?.find((f) => f.toppingPid === localFlavor.value?.toppingPid);
                });
            }
        );

        watch(localFlavor, () => emit('blockSubmit', false));

        return {
            localFlavor,
            hasAvailableDressings,
            hasAvailableFlavors,
            skeleText,
            isLoader,
            isCartLoader,
            selectedToppingOptions,
            cupLimit,
            getToppingOptions,
            updateChickenToppings,
        };
    },
});
</script>
